import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List, useDataGrid } from "@refinedev/mui";
import { Link as RouterLink } from "react-router";
import {
  EVIDENCE_RESOURCE,
  FRAMING_LABEL,
  OUTCOME_LABEL,
  TARGET_LABEL,
  TRIGGER_LABEL,
  formatScore,
  reviewClock,
  type EvidenceHoldListItem,
  type EvidenceHoldStatus,
  type EvidencePreserveState,
} from "@/lib/evidence";

const STATUSES: readonly EvidenceHoldStatus[] = ["active", "reported", "released"];

const STATUS_COLOR: Record<EvidenceHoldStatus, "warning" | "info" | "default"> = {
  active: "warning",
  reported: "info",
  released: "default",
};

const PRESERVE_COLOR: Record<EvidencePreserveState, "success" | "warning" | "error"> = {
  preserved: "success",
  pending: "warning",
  failed: "error",
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/**
 * Evidence holds. Ids, clocks and decisions only: the list never shows a
 * thumbnail, a path or a URL, and the server never sends one. The material is
 * on each hold's own page, behind a reveal.
 */
export const EvidenceHoldList = () => {
  const [status, setStatus] = React.useState<string>("active");

  const { dataGridProps } = useDataGrid<EvidenceHoldListItem>({
    resource: EVIDENCE_RESOURCE,
    filters: {
      permanent: [{ field: "status", operator: "eq" as const, value: status || undefined }],
    },
    queryOptions: { refetchInterval: 60_000 },
  });

  const columns = React.useMemo<GridColDef<EvidenceHoldListItem>[]>(
    () => [
      {
        field: "publicId",
        headerName: "Hold",
        flex: 0.9,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Link component={RouterLink} to={`/evidence/${row.publicId}`} sx={{ fontFamily: "monospace" }}>
            {row.publicId}
          </Link>
        ),
      },
      {
        field: "reviewDueAt",
        headerName: "Clock",
        flex: 0.8,
        minWidth: 150,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => {
          if (row.reviewDueAt) {
            const clock = reviewClock(row.reviewDueAt);
            return (
              <Chip
                size="small"
                label={clock.label}
                color={clock.color}
                variant={clock.color === "default" ? "outlined" : "filled"}
              />
            );
          }
          return (
            <Typography variant="caption" color="text.secondary">
              {row.reviewOutcome ? OUTCOME_LABEL[row.reviewOutcome] : "-"}
            </Typography>
          );
        },
      },
      {
        field: "status",
        headerName: "State",
        flex: 0.9,
        minWidth: 190,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Chip size="small" label={row.status} color={STATUS_COLOR[row.status]} />
            <Chip
              size="small"
              variant="outlined"
              label={row.preserveState}
              color={PRESERVE_COLOR[row.preserveState]}
            />
          </Stack>
        ),
      },
      {
        field: "targetType",
        headerName: "Target",
        flex: 1,
        minWidth: 190,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {TARGET_LABEL[row.targetType]} <code>{row.targetRef}</code>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {TRIGGER_LABEL[row.trigger]}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "subjectUserId",
        headerName: "Account",
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{row.subjectUserId ?? "-"}</Typography>,
      },
      {
        field: "minorScore",
        headerName: "Classifier",
        flex: 0.9,
        minWidth: 200,
        sortable: false,
        display: "flex",
        // What the check scored, and for a picture check the framing that adds up to: "minor" alone reads as sexual
        // content whatever the explicit score was.
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              minor {formatScore(row.minorScore)}
              {row.explicitScore != null && ` · explicit ${formatScore(row.explicitScore)}`}
            </Typography>
            {row.framing && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {FRAMING_LABEL[row.framing]}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "objectCount",
        headerName: "Objects",
        flex: 0.4,
        minWidth: 80,
        sortable: false,
        display: "flex",
      },
      {
        field: "placedAt",
        headerName: "Placed",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{formatDateTime(row.placedAt)}</Typography>,
      },
      {
        field: "preserveUntil",
        headerName: "Preserve until",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{formatDateTime(row.preserveUntil)}</Typography>,
      },
    ],
    []
  );

  return (
    <List canCreate={false} title="Evidence holds">
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Apparent CSAM and its context, held under 18 U.S.C. § 2258A(h). Each hold is owed a review outcome
          within 24 hours. Open the material only on a hold&apos;s own page — never download it, screenshot it, or
          send it anywhere, including to a colleague, to counsel, or to NCMEC by email.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <DataGrid
        {...dataGridProps}
        getRowId={(row) => row.publicId}
        columns={columns}
        disableColumnFilter
        rowHeight={60}
      />
    </List>
  );
};
